<template>
  <article
    class="ls-card"
    :class="{ 'ls-card--large': large }"
  >
    <div class="ls-card__media">
      <button
        type="button"
        class="ls-card__open"
        data-test="ls-card-open"
        :aria-label="`View ${name}`"
        @click="$emit('open', product._id)"
      >
        <template v-if="image">
          <img
            class="ls-card__img"
            :class="{ 'is-contain': wide }"
            :src="image"
            :alt="name"
            loading="lazy"
            decoding="async"
            @load="measure"
            @error="failed = true"
          >
          <img
            v-if="secondImage"
            class="ls-card__img ls-card__img--alt"
            :src="secondImage"
            alt=""
            loading="lazy"
            decoding="async"
            aria-hidden="true"
          >
        </template>
        <span
          v-else
          class="ls-card__tile"
          data-test="ls-card-tile"
        >{{ name }}</span>
      </button>
      <button
        type="button"
        class="ls-card__heart"
        data-test="ls-card-heart"
        :aria-pressed="shortlisted ? 'true' : 'false'"
        :aria-label="`${shortlisted ? 'Remove' : 'Add'} ${name} ${shortlisted ? 'from' : 'to'} shortlist`"
        @click="$emit('toggle', product._id)"
      >
        <svg
          viewBox="0 0 24 24"
          width="20"
          height="20"
          aria-hidden="true"
          focusable="false"
        ><path
          d="M12 20.5s-7.5-4.6-7.5-10.1A4.4 4.4 0 0 1 12 7.6a4.4 4.4 0 0 1 7.5 2.8c0 5.5-7.5 10.1-7.5 10.1Z"
          :fill="shortlisted ? 'currentColor' : 'none'"
          stroke="currentColor"
          stroke-width="1.6"
          stroke-linejoin="round"
        /></svg>
      </button>
    </div>
    <div class="ls-card__body">
      <h3 class="ls-card__name">
        {{ name }}
        <span
          v-if="feedbackOn && hasComment"
          class="ls-card__note-mark"
          data-test="ls-card-note-mark"
        >
          <svg
            viewBox="0 0 24 24"
            width="14"
            height="14"
            aria-hidden="true"
            focusable="false"
          ><path
            d="M5 5h14v10H10l-4 4v-4H5z"
            fill="none"
            stroke="currentColor"
            stroke-width="1.6"
            stroke-linejoin="round"
          /></svg>
          <span class="ls-card__sr">You left a note</span>
        </span>
      </h3>
      <p
        v-if="line"
        class="ls-card__line"
        data-test="ls-card-line"
      >{{ line }}</p>
      <ul
        v-if="colours.length"
        class="ls-card__colours"
        data-test="ls-card-colours"
        :aria-label="`Colours: ${colours.map(c => c.name).join(', ')}`"
      >
        <li
          v-for="colour in shownColours"
          :key="colour.name"
          class="ls-card__swatch"
          :style="{ background: colour.swatch }"
          :title="colour.name"
          aria-hidden="true"
        />
        <li
          v-if="colours.length > shownColours.length"
          class="ls-card__more"
          aria-hidden="true"
        >+{{ colours.length - shownColours.length }}</li>
      </ul>
      <p
        v-if="prices.length"
        class="ls-card__prices"
        data-test="ls-card-prices"
      >
        <span
          v-for="price in prices"
          :key="price.kind"
          class="ls-card__price"
        ><span class="ls-card__price-label">{{ price.label }}</span> {{ price.text }}</span>
      </p>
      <span
        v-if="moq"
        class="ls-card__moq"
        data-test="ls-card-moq"
      >Min. {{ moq }} per style</span>
      <button
        v-if="feedbackOn"
        type="button"
        class="ls-card__interest"
        data-test="ls-card-interest"
        :aria-pressed="interested ? 'true' : 'false'"
        :aria-label="`Interested in ${name}`"
        @click="$emit('interest', product._id)"
      >
        <svg
          viewBox="0 0 24 24"
          width="14"
          height="14"
          aria-hidden="true"
          focusable="false"
        ><path
          :d="interested ? 'M5 12.5l4.5 4.5L19 7.5' : 'M12 5v14M5 12h14'"
          fill="none"
          stroke="currentColor"
          stroke-width="1.8"
          stroke-linecap="round"
          stroke-linejoin="round"
        /></svg>
        <span>Interested</span>
      </button>
    </div>
  </article>
</template>

<script>
import { getColorCode } from '@/product-colors'
import { productPrices } from './priceFormat'
import { productColours, productImages } from './lineSheetModel'

const MAX_SWATCHES = 5
// A picture wider than this (width / height) is shown whole rather than
// cropped to the 4:5 frame: a landscape packshot or a wordmark would lose
// most of itself to the crop.
const MAX_COVER_RATIO = 1.25
const NEUTRAL_SWATCH = '#c9c4bc'

export default {
  name: 'LsProductCard',
  props: {
    product: { type: Object, required: true },
    line: { type: String, default: '' },
    priceVisibility: { type: Object, default: undefined },
    shortlisted: { type: Boolean, default: false },
    large: { type: Boolean, default: false },
    // KP-4572: interest and the note mark show only while feedback is available.
    feedbackOn: { type: Boolean, default: false },
    interested: { type: Boolean, default: false },
    hasComment: { type: Boolean, default: false },
  },
  data() {
    return { failed: false, wide: false }
  },
  computed: {
    name() {
      return this.product.name || 'Untitled style'
    },
    images() {
      return productImages(this.product)
    },
    image() {
      return this.failed ? '' : this.images[0] || ''
    },
    secondImage() {
      return this.images[1] || ''
    },
    colours() {
      return productColours(this.product).map(c => ({
        ...c,
        swatch: c.hex || (c.family && getColorCode(c.family)) || getColorCode(c.name) || NEUTRAL_SWATCH,
      }))
    },
    shownColours() {
      return this.colours.slice(0, MAX_SWATCHES)
    },
    prices() {
      return productPrices(this.product, this.priceVisibility)
    },
    moq() {
      const n = Number(this.product.minQtyPerStyle)
      return Number.isFinite(n) && n > 0 ? n : 0
    },
  },
  methods: {
    measure(event) {
      const { naturalWidth: w, naturalHeight: h } = event.target
      this.wide = h > 0 && w / h > MAX_COVER_RATIO
    },
  },
}
</script>

<style lang="scss" scoped>
@import './ls-base';

.ls-card {
  display: flex;
  flex-direction: column;
  height: 100%;
  color: var(--ls-ink);

  &__media {
    position: relative;
    aspect-ratio: 4 / 5;
    background: var(--ls-surface);
    border-radius: var(--ls-radius);
    overflow: hidden;
  }

  &__open {
    position: absolute;
    inset: 0;
    display: block;
    width: 100%;
    padding: 0;
    border: 0;
    background: none;
    cursor: pointer;

    &:focus-visible {
      outline: 2px solid var(--ls-ink);
      outline-offset: -4px;
    }
  }

  &__img {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    animation: ls-card-fade 0.2s $ls-ease both;
    transition: opacity 0.35s $ls-ease, transform 0.6s $ls-ease;

    &--alt {
      opacity: 0;
    }

    &.is-contain {
      object-fit: contain;
      padding: 8%;
    }
  }

  // The second photograph shows on hover or keyboard focus, where the device
  // can hover; touch screens keep the first.
  @media (hover: hover) {
    &__open:hover &__img,
    &__open:focus-visible &__img {
      transform: scale(1.02);
    }

    &__open:hover &__img--alt,
    &__open:focus-visible &__img--alt {
      opacity: 1;
    }
  }

  &__tile {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 16%;
    background: var(--ls-surface-2);
    color: var(--ls-ink);
    @include ls-heading;
    font-size: clamp(1.125rem, 2.4vw, 1.625rem);
    text-align: center;
    overflow-wrap: anywhere;
  }

  &__heart {
    position: absolute;
    top: 6px;
    right: 6px;
    z-index: 2;
    display: grid;
    place-items: center;
    width: 44px;
    height: 44px;
    padding: 0;
    border: 0;
    border-radius: 999px;
    background: var(--ls-ground);
    color: var(--ls-ink);
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.12);
    cursor: pointer;
    touch-action: manipulation;
    transition: transform 0.2s $ls-ease;
    @include ls-focus;

    &:active {
      transform: scale(0.94);
    }

    &[aria-pressed='true'] {
      color: var(--ls-accent);
    }
  }

  &__body {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding-top: 14px;
  }

  &__name {
    margin: 0;
    color: var(--ls-ink);
    font: 600 0.9375rem/1.3 var(--ls-body-font);
    letter-spacing: var(--ls-body-tracking);
    overflow-wrap: anywhere;
  }

  &__line {
    margin: 0;
    color: var(--ls-muted);
    font: 400 0.875rem/1.5 var(--ls-body-font);
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  &__colours {
    display: flex;
    align-items: center;
    gap: 6px;
    margin: 0;
    padding: 0;
    list-style: none;
  }

  &__swatch {
    width: 12px;
    height: 12px;
    border-radius: 999px;
    box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.18);
  }

  &__more {
    color: var(--ls-muted);
    font: 500 0.75rem/1 var(--ls-body-font);
  }

  &__prices {
    display: flex;
    flex-wrap: wrap;
    gap: 2px 14px;
    margin: 0;
    color: var(--ls-ink);
    font: 500 0.875rem/1.4 var(--ls-body-font);
    font-variant-numeric: tabular-nums;
  }

  &__price-label {
    color: var(--ls-muted);
    font-weight: 400;
  }

  &__moq {
    align-self: flex-start;
    padding: 4px 8px;
    border: 1px solid var(--ls-line);
    border-radius: 999px;
    color: var(--ls-ink);
    font: 500 0.75rem/1 var(--ls-body-font);
  }

  &__note-mark {
    display: inline-flex;
    vertical-align: -2px;
    margin-left: 4px;
    color: var(--ls-muted);
  }

  &__sr {
    @include ls-sr-only;
  }

  // A quiet text toggle: it must not compete with the heart or the prices.
  &__interest {
    align-self: flex-start;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    min-height: 44px;
    margin: -8px 0 -10px -2px;
    padding: 0 2px;
    border: 0;
    background: transparent;
    color: var(--ls-muted);
    font: 500 0.8125rem/1 var(--ls-body-font);
    cursor: pointer;
    touch-action: manipulation;
    @include ls-focus;

    &:hover {
      color: var(--ls-ink);
    }

    &[aria-pressed='true'] {
      color: var(--ls-ink);
      font-weight: 600;
    }
  }

  &--large &__name {
    font-size: 1.0625rem;
  }

  &--large &__line {
    font-size: 0.9375rem;
  }
}

@keyframes ls-card-fade {
  from {
    opacity: 0;
  }
}

@media (prefers-reduced-motion: reduce) {
  .ls-card__img,
  .ls-card__heart {
    animation: none;
    transition: none;
    transform: none !important;
  }
}
</style>
